<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { useNotificationsStore } from 'stores/notifications.js'

const props = defineProps({
  userId:     { type: [Number, String], required: true },
  profileUrl: { type: String, default: null },
})

const store      = useNotificationsStore()
const show       = ref(false)
const card       = computed(() => store.userCards[props.userId])
const triggerRef = ref(null)
const pos        = ref({ verticalPos: 0, left: 0, above: true, arrowLeft: 20 })

let hideTimer = null
let showTimer = null

function reposition() {
  if (!triggerRef.value) return
  const rect          = triggerRef.value.getBoundingClientRect()
  const cardW         = 256
  const cardH         = 250
  const spaceAbove    = rect.top
  const spaceBelow    = window.innerHeight - rect.bottom
  const above         = spaceAbove >= cardH + 16 && spaceAbove >= spaceBelow
  const triggerCenter = rect.left + rect.width / 2
  const left          = Math.min(Math.max(8, triggerCenter - cardW / 2), window.innerWidth - cardW - 8)
  const arrowLeft     = Math.max(12, Math.min(Math.round(triggerCenter - left - 6), cardW - 24))

  pos.value = {
    left,
    // above: pin card bottom 8px above trigger (CSS bottom = distance from viewport bottom)
    // below: pin card top 8px below trigger (CSS top)
    verticalPos: above ? window.innerHeight - rect.top + 8 : rect.bottom + 8,
    above,
    arrowLeft,
  }
}

function onEnter() {
  clearTimeout(hideTimer)
  showTimer = setTimeout(() => {
    reposition()
    show.value = true
    store.fetchUserCard(props.userId)
  }, 350)
}

function onLeave() {
  clearTimeout(showTimer)
  hideTimer = setTimeout(() => { show.value = false }, 180)
}

function onPopoverEnter() { clearTimeout(hideTimer) }
function onPopoverLeave() { hideTimer = setTimeout(() => { show.value = false }, 180) }

onUnmounted(() => { clearTimeout(hideTimer); clearTimeout(showTimer) })
</script>

<template>
  <span class="relative inline-block">
    <!-- Trigger name -->
    <a ref="triggerRef"
       :href="profileUrl"
       target="_blank"
       rel="noopener noreferrer"
       class="font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400
              transition-colors cursor-pointer"
       @mouseenter="onEnter"
       @mouseleave="onLeave">
      <slot />
    </a>

    <Teleport to="body">
      <!-- Outer wrapper: no overflow-hidden so the caret can stick out -->
      <div v-if="show"
           :style="pos.above
             ? { position: 'fixed', left: pos.left + 'px', bottom: pos.verticalPos + 'px', zIndex: 9999, width: '256px' }
             : { position: 'fixed', left: pos.left + 'px', top: pos.verticalPos + 'px', zIndex: 9999, width: '256px' }"
           style="filter: drop-shadow(0 8px 24px rgba(0,0,0,0.14)) drop-shadow(0 2px 6px rgba(0,0,0,0.08))"
           @mouseenter="onPopoverEnter"
           @mouseleave="onPopoverLeave">

        <!-- Caret pointing DOWN (card is above trigger) -->
        <div v-if="pos.above"
             class="absolute -bottom-[6px] w-3 h-3 rotate-45
                    bg-white dark:bg-[#1C2128]
                    border-r border-b border-gray-300 dark:border-gray-500"
             :style="{ left: pos.arrowLeft + 'px' }" />

        <!-- Caret pointing UP (card is below trigger) -->
        <div v-else
             class="absolute -top-[6px] w-3 h-3 rotate-45
                    bg-white dark:bg-[#1C2128]
                    border-l border-t border-gray-300 dark:border-gray-500"
             :style="{ left: pos.arrowLeft + 'px' }" />

        <!-- Card content — overflow-hidden + border here, not on outer wrapper -->
        <div class="w-full rounded-xl overflow-hidden
                    bg-white dark:bg-[#1C2128]
                    border border-gray-300 dark:border-gray-500">

          <!-- Loading skeleton -->
          <div v-if="!card || card.loading" class="p-4 animate-pulse">
            <div class="flex items-center gap-3 mb-3">
              <div class="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
              <div class="flex-1 space-y-2">
                <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-1/2" />
              </div>
            </div>
            <div class="space-y-1.5">
              <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-full" />
              <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-5/6" />
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="card.error"
               class="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
            Could not load profile.
          </div>

          <!-- Loaded -->
          <div v-else-if="card.data">
            <div class="p-4">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <img v-if="card.data.profile_picture"
                       :src="card.data.profile_picture"
                       alt=""
                       class="w-12 h-12 rounded-full object-cover
                              border border-gray-200 dark:border-gray-600" />
                  <div v-else
                       class="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700
                              flex items-center justify-center
                              text-gray-600 dark:text-gray-300 font-semibold text-base
                              border border-gray-200 dark:border-gray-600">
                    {{ card.data.avatar_name }}
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-1">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
                      {{ card.data.full_name }}
                    </p>
                    <span v-if="card.data.verified" class="flex-shrink-0">
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                        <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                  </div>
                  <p v-if="card.data.designation"
                     class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {{ card.data.designation }}
                  </p>
                </div>
              </div>
              <p v-if="card.data.bio"
                 class="mt-2.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
                {{ card.data.bio }}
              </p>
            </div>

            <!-- View profile button -->
            <div class="px-4 pb-3 border-t border-gray-100 dark:border-gray-700 pt-3">
              <a :href="card.data.profile_url"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg
                        text-xs font-semibold text-blue-600 dark:text-blue-400
                        ring-1 ring-blue-200 dark:ring-blue-800/60
                        hover:bg-blue-50 dark:hover:bg-blue-900/20
                        transition-colors no-underline">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                View profile
              </a>
            </div>
          </div>

        </div>
      </div>
    </Teleport>
  </span>
</template>
